import { skipToken, useSuspenseQuery } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import * as React from 'react';

import type { NavigationScreenState } from '@gitbook/app-navigation';
import {
    CollapsibleSidebar,
    LoadingPanel,
    ResizableSidebar,
    SidePanelHostContext,
    useScreenType,
} from '@gitbook/spine-react';

import { AgentDockSlot, PaymentFailedAlertBanner, useAgentDockLayout } from '../Components';
import { shortcuts, useKeyboardShortcuts } from '../Keyboard';
import {
    apiQuery,
    desktopSidebarCollapsedAtom,
    desktopSidebarWidthAtom,
    useCurrentOrganizationOrNull,
    useIsSidebarLocked,
    useLockSidebar,
    useUnlockSidebar,
} from '../state';
import { AppBanner } from './AppBanner';
import { AppLayoutRail } from './AppLayoutRail';
import { MobileHeader } from './MobileHeader';
import { RealtimeSyncBanner, RealtimeSyncReporter } from './RealtimeSyncBanner';

interface LayoutSidebarContextValue {
    isCollapsed: boolean;
}

/**
 * Global context of the rendering mode of the application.
 */
export const LayoutSidebarContext = React.createContext<LayoutSidebarContextValue>({
    isCollapsed: false,
});

/**
 * Main application layout with the sidebar.
 */
export function AppLayout(props: {
    screen: NavigationScreenState;
    sidebar: React.ReactNode;
    children: React.ReactNode;
    /**
     * True for the copy of the layout an overlay screen renders over the one underneath, which does
     * without the parts of the frame the screen below is already holding.
     */
    isOverlay?: boolean;
}): React.ReactElement {
    const { sidebar, screen, children, isOverlay = false } = props;
    const [isMenuOpen, setMenuOpen] = React.useState(false);
    const [isCollapsed, setCollapse] = useAtom(desktopSidebarCollapsedAtom);
    const [width, setWidth] = useAtom(desktopSidebarWidthAtom);
    const { isMobile } = useScreenType();
    const withRail = useAppLayoutRail(isOverlay);
    // The rail's host, handed to the side panels through the context below. Held here rather than
    // in the rail because the panel and the rail have to agree on it within one render.
    const [railHost, setRailHost] = React.useState<HTMLDivElement | null>(null);
    // The rail is another column asking for the width the sidebar was using, so it asks for the
    // sidebar the same way a page's side panel does — and on the same terms, since how wide a
    // window has to be before nothing is taken away is the lock's rule rather than each caller's.
    // No callback to undo it with: a user who insists on the sidebar gets it, and the agent stays
    // where they put it rather than being sent back to the dock behind their back.
    useLockSidebar(withRail);
    // Offered from the first render the rail appears in, and carrying the element only once the
    // rail has attached it a render later. Announcing the column before it can be pointed at is
    // what keeps a panel that is already open — a page opened straight onto its comments, say —
    // from being built over the page and immediately rebuilt in here.
    const sidePanelHost = React.useMemo(
        () => (withRail ? { element: railHost } : null),
        [withRail, railHost]
    );
    const sidebarContext = React.useMemo(() => ({ isCollapsed }), [isCollapsed]);

    return (
        <div className="relative flex min-h-0 min-w-0 flex-1 flex-col bg-side">
            <AppBanner />
            <RealtimeSyncReporter />
            <RealtimeSyncBanner />
            <AppLayoutPaymentFailedBanner screen={screen} />
            <div className="flex min-h-0 min-w-0 flex-1">
                {process.env.GITBOOK_PREDICTABLE_UI || isMobile ? (
                    <MobileSidebar isOpen={isMenuOpen} onOpenChange={setMenuOpen}>
                        {sidebar}
                    </MobileSidebar>
                ) : null}
                {process.env.GITBOOK_PREDICTABLE_UI || !isMobile ? (
                    <DesktopSidebar
                        isCollapsed={isCollapsed}
                        onCollapseChange={setCollapse}
                        width={width}
                        onWidthChange={setWidth}
                    >
                        {sidebar}
                    </DesktopSidebar>
                ) : null}
                <LayoutSidebarContext.Provider value={sidebarContext}>
                    {/* Offered to the screen's side panels, which render into the rail instead of
                        over the page whenever there is one. Null without a rail, which is what
                        leaves them overlaying the page as they always have. */}
                    <SidePanelHostContext.Provider value={sidePanelHost}>
                        <div
                            className="@container/main relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
                            data-testid="appLayout.content"
                        >
                            <React.Suspense
                                fallback={
                                    <AppFraming>
                                        <LoadingPanel size="xlarge" />
                                    </AppFraming>
                                }
                            >
                                {isMobile && (
                                    <MobileHeader
                                        onMenuOpenChange={setMenuOpen}
                                        isMenuOpen={isMenuOpen}
                                        screen={screen}
                                    />
                                )}
                                <AppFraming>{children}</AppFraming>
                            </React.Suspense>
                            {/* Sits beside the sidebar rather than under it, so the sidebar keeps the
                                full window height and the dock only spans the content. */}
                            <AgentDockSlot kind="bar" className="relative shrink-0" />
                        </div>
                        {withRail ? <AppLayoutRail hostRef={setRailHost} /> : null}
                    </SidePanelHostContext.Provider>
                </LayoutSidebarContext.Provider>
            </div>
        </div>
    );
}

/**
 * Whether the layout keeps its right rail: the column the page's side panel and the anchored agent
 * share.
 *
 * Answered while rendering rather than from an effect, so that the side panels are told about the
 * rail in the very render it appears in. Told a render later, an open panel would mount over the
 * page and move here afterwards — rebuilding itself, and everything it holds, on the way.
 */
function useAppLayoutRail(isOverlay: boolean): boolean {
    const organization = useCurrentOrganizationOrNull();
    const { mode } = useAgentDockLayout();

    // Not in an overlay: it renders a second copy of the layout over the one underneath, whose rail
    // is still there — and covered — exactly as the dock's bar is. And not without the agent, since
    // a column reserved for it would only take width from an organization that cannot use it.
    return !isOverlay && mode === 'anchored' && Boolean(organization?.ai);
}

function AppFraming(props: { children: React.ReactNode }) {
    const { children } = props;
    return (
        <div
            // The app framing styles can't be applied in a single location with our current DOM architecture. Screens with sidepanels require applying the framing styles on a more local level instead of in AppLayout.
            className="md:[&:not(:has([data-side-panel-frame]))]:app-framing-styles flex min-h-0 min-w-0 flex-1 flex-col md:ml-1.5 sidebar-expanded:ml-0"
        >
            {children}
        </div>
    );
}

function AppLayoutPaymentFailedBanner(props: { screen: NavigationScreenState }) {
    const { screen } = props;
    const organizationId =
        'organization' in screen && screen.organization ? screen.organization : null;
    const { data: organization } = useSuspenseQuery(
        apiQuery('getOrganizationById', organizationId ? { organizationId } : skipToken)
    );

    if (!organization?.billing?.hasPaymentFailed) {
        return null;
    }

    return (
        <PaymentFailedAlertBanner
            organization={organization}
            analyticsReason="app_top_banner_cta_payment_failure"
        />
    );
}

function MobileSidebar(props: {
    children: React.ReactNode;
    isOpen: boolean;
    onOpenChange: (opened: boolean) => void;
}) {
    const { children, isOpen, onOpenChange } = props;
    return (
        <CollapsibleSidebar isOpen={isOpen} onOpenChange={onOpenChange}>
            {children}
        </CollapsibleSidebar>
    );
}

function DesktopSidebar(props: {
    children: React.ReactNode;
    isCollapsed: boolean;
    onCollapseChange: (collapsed: boolean) => void;
    width: number | null;
    onWidthChange: (width: number | null) => void;
}) {
    const { children, isCollapsed, onCollapseChange, width, onWidthChange } = props;
    const locked = useIsSidebarLocked();
    const unlock = useUnlockSidebar();

    const { isTablet } = useScreenType();

    useKeyboardShortcuts(
        {
            [shortcuts.TOGGLE_SIDEBAR]: () => {
                onCollapseChange(!isCollapsed);
            },
        },
        ['application', 'editor-selection-collapsed']
    );
    return (
        <ResizableSidebar
            isCollapsed={locked || isCollapsed}
            onCollapseChange={(collapsed) => {
                if (!collapsed) {
                    unlock();
                }
                onCollapseChange(collapsed);
            }}
            width={width}
            onWidthChange={onWidthChange}
            expandMode={isTablet ? 'manual' : 'auto'}
            className="max-md:hidden"
        >
            {children}
        </ResizableSidebar>
    );
}
